import { useEffect, useState } from 'react';
import { TextField, Typography, Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { CircularProgress } from '@material-ui/core';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

interface CatalogApplication {
  name: string;
  repo: string;
  description?: string;
  label: string;
}

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  option: {
    display: 'flex',
    flexDirection: 'column',
  },
  optionPrimary: {
    fontWeight: 500,
  },
  optionSecondary: {
    fontSize: '0.85em',
    color: theme.palette.text.secondary,
  },
}));

/**
 * Scaffolder field extension that lets users search and select an application
 * from the Backstage catalog. On selection, it auto-fills the githubRepository
 * and applicationName sibling fields via formContext.
 */
export function ApplicationPicker(props: FieldExtensionComponentProps<string>) {
  const { onChange, formContext, schema } = props;
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);

  const [options, setOptions] = useState<CatalogApplication[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    catalogApi
      .getEntities({ filter: { kind: 'Component' } })
      .then(response => {
        if (cancelled) return;

        const apps: CatalogApplication[] = response.items
          .map(entity => {
            const name = entity.metadata.name;
            const slug =
              entity.metadata.annotations?.['github.com/project-slug'] ?? '';
            const repo = slug.replace('Mercell/', '');
            const description = entity.metadata.description ?? undefined;
            return {
              name,
              repo,
              ...(description !== undefined && { description }),
              label: repo ? `${repo} / ${name}` : name,
            };
          })
          .sort((a, b) => a.label.localeCompare(b.label));

        setOptions(apps);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [catalogApi]);

  const handleChange = (
    _event: object,
    value: CatalogApplication | string | null,
  ) => {
    if (!value) {
      onChange(undefined);
      return;
    }

    if (typeof value === 'string') {
      // freeSolo typed input — store raw value, don't auto-fill siblings
      onChange(value);
      return;
    }

    // Auto-fill sibling fields via formContext.onChange (RJSF pattern)
    const formData = (formContext?.formData ?? {}) as Record<string, unknown>;
    const onFormChange = (formContext as Record<string, unknown> | undefined)
      ?.onChange as ((data: Record<string, unknown>) => void) | undefined;

    if (onFormChange) {
      onFormChange({
        ...formData,
        githubRepository: value.repo,
        applicationName: value.name,
      });
    }
    onChange(value.label);
  };

  return (
    <Box className={classes.root}>
      <Autocomplete
        freeSolo
        options={options}
        loading={loading}
        getOptionLabel={option =>
          typeof option === 'string' ? option : option.label
        }
        renderOption={option => (
          <Box className={classes.option}>
            <Typography className={classes.optionPrimary}>
              {option.label}
            </Typography>
            {option.description && (
              <Typography className={classes.optionSecondary}>
                {option.description}
              </Typography>
            )}
          </Box>
        )}
        renderInput={params => (
          <TextField
            {...params}
            size={params.size ?? 'medium'}
            label={schema.title ?? 'Search Catalog'}
            {...(schema.description && { helperText: schema.description })}
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        onChange={handleChange}
      />
    </Box>
  );
}
